import { Theme } from "@mui/material";
import { TooltipComponentOption } from "echarts";
import { round } from "lodash";

import { WaterfallChartDataItem } from "@/Components/v2/molecule/WaterfallChart/WaterfallChart";
import { FormatCurrencyFunction } from "@/hooks/useConfig/types";
import { MetricType } from "@/shared/types";

const getContributionString = (value: number, total: number, metricType?: MetricType) => {
  const percentage = round((value / total) * 100, 2);
  const valueString = Math.abs(percentage) < 0.01 && value !== 0 ? "< 0.01%" : `${percentage}%`;
  if (!metricType) return `(${valueString})`;
  const metricString = metricType === MetricType.SALES_UNITS ? "total sales" : "total revenue";
  return `(${valueString} of ${metricString})`;
};

const renderChildren = (
  children: WaterfallChartDataItem[],
  totalRevenue: number,
  metricFormatter: (value: number) => string,
  theme: Theme
) => {
  if (children.length === 0) return "";
  return `
    <div>
      <div style='margin: 12px 0; border-bottom: 1px solid ${theme.palette.divider};'></div>
      <div style='display: flex; flex-direction: column; gap: 8px;'>
        ${children
          .map(
            (child) => `
            <div>
              <div style='font-weight: 600'>${child.name}</div>
              <div>${metricFormatter(child.value)} ${getContributionString(child.value, totalRevenue)}</div>
            </div>
          `
          )
          .join("")}
      </div>
    </div>
  `;
};

export const tooltipOption = (
  theme: Theme,
  seriesData: WaterfallChartDataItem[],
  totalRevenue: number,
  metricFormatter: (value: number) => string,
  metricType: MetricType
): TooltipComponentOption => ({
  formatter: (params: any) => {
    const target = params[1];
    const data = seriesData.find((item) => item.name === target.name) ?? {
      value: totalRevenue,
      children: [],
    };

    return `
      <div style='border-left: 4px solid ${target.color}; padding: 16px; border-radius: 4px 0 0 4px;'>
        <div style='font-weight: 600; font-size: 16px; margin-bottom: 6px;'>${target.name}</div>
        <div style='font-size: 14px;'>${metricFormatter(data?.value ?? 0)}</div>
        <div style='font-size: 14px;'>${getContributionString(data?.value ?? 0, totalRevenue, metricType)}</div>
        ${renderChildren(data?.children ?? [], totalRevenue, metricFormatter, theme)}
      </div>
    `;
  },
});

export const getBarLabel = (
  theme: Theme,
  seriesData: WaterfallChartDataItem[],
  totalRevenue: number,
  metricFormatter: FormatCurrencyFunction
) => ({
  show: true,
  position: "top",
  offset: [0, -15],
  color: theme.palette.brandedTypography?.textColor,
  fontWeight: "bold",
  fontSize: 12,
  formatter: (params: any) => {
    const actualValue = seriesData.find((item) => item.name === params.name)?.value ?? totalRevenue;

    const percentage = round((actualValue / totalRevenue) * 100, 2);
    const lines = [
      "{bold|" +
        `${Math.abs(percentage) > 0.01 && percentage !== 0 ? percentage : "< 0.01"}%` +
        "}",
    ];
    lines.push("{normal|" + metricFormatter(actualValue) + "}");
    return lines.join("\n");
  },
  rich: {
    bold: {
      fontWeight: "bold",
      fontSize: 12,
      padding: [4, 0],
    },
    normal: {
      fontWeight: "normal",
      fontSize: 12,
    },
  },
  align: "center",
  verticalAlign: "middle",
});
